// Integração com o widget de suporte bFocus (Berni Software).
// O widget é SEMPRE flutuante (shadow DOM, posição fixa) — não há modo inline/embed.
// Por isso: no desktop usamos o launcher flutuante; no mobile não carregamos o
// launcher (libera a tela) e abrimos o painel sob demanda pela tela de Suporte.

const WIDGET_KEY = (import.meta.env.VITE_BFOCUS_WIDGET_KEY as string | undefined) ?? '';
const WIDGET_URL = (import.meta.env.VITE_BFOCUS_WIDGET_URL as string | undefined) ?? 'https://widget.bfocus.com.br/v1/widget.js';
const SCRIPT_ID = 'bfocus-support-widget';
// Monitoramento do bFocus pelo próprio widget (`monitor` no init): os erros de front do
// painel viram demanda no bFocus, ligados à identidade assinada de sempre. Chave pública
// (bf_mon_) do agente, BUILD_TIME; vazia = sem monitor (o init fica como era).
const MONITOR_KEY = ((import.meta.env.VITE_BFOCUS_MONITOR_KEY as string | undefined) ?? '').trim();
// Versão do bZapper (vite.config.ts lê do OpenAPI, a mesma da API).
const RELEASE = (import.meta.env.VITE_BZAPPER_RELEASE as string | undefined) || undefined;
// Mesmo critério do analytics: produção é só o domínio do painel.
const PROD_HOSTNAME = 'app.bzapper.com.br';

interface InitOpts {
  key: string;
  user: { externalId: string; name?: string; email?: string };
  customer: { externalId: string; name?: string };
  userHash?: string;
  product?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  locale?: 'pt_BR' | 'en' | 'es';
  role?: { key: string; label?: string };
  monitor?: { key: string; release?: string; environment?: string };
}
declare global {
  interface Window {
    bFocus?: { init: (o: InitOpts) => void; open?: () => void; close?: () => void; destroy?: () => void };
    bFocusQueue?: unknown[];
  }
}

export interface BFocusMe {
  user_id?: string; email?: string; tenant_id?: string; tenant_name?: string; name?: string; locale?: string;
  role?: string;
  bfocus?: { user_external_id: string; customer_external_id: string; user_hash: string };
}

// Papéis do bZapper (auth.Role na API) → rótulo que o bFocus mostra na tela e
// entrega ao agente de IA. O rótulo é gravado na PRIMEIRA vez que o papel chega
// (o bFocus cadastra e vincula sozinho), então vale mandá-lo legível de saída.
// Sempre em português: é um rótulo único do produto, não a tela deste usuário.
// Papel fora deste mapa (ou ausente) não vira `role` — melhor nada do que um
// papel inventado no cadastro do produto.
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Administrador da plataforma',
  admin: 'Administrador',
  agent: 'Membro',
};

export function bfocusEnabled(): boolean { return !!WIDGET_KEY; }

function widgetLocale(l?: string): 'pt_BR' | 'en' | 'es' {
  if (l?.startsWith('pt')) return 'pt_BR';
  if (l?.startsWith('es')) return 'es';
  return 'en';
}

/** `{ key, label }` do papel declarado, ou undefined se não houver mapa para ele. */
function widgetRole(role?: string): { key: string; label: string } | undefined {
  const key = (role ?? '').trim().toLowerCase();
  const label = ROLE_LABELS[key];
  return label ? { key, label } : undefined;
}

/** Carrega o script (idempotente) e inicializa o widget para o usuário logado. */
export function bfocusInit(me: BFocusMe): void {
  if (!WIDGET_KEY || !me) return;
  if (!window.bFocus) {
    window.bFocusQueue = window.bFocusQueue || [];
    window.bFocus = { init: (o) => window.bFocusQueue!.push(['init', o]) };
  }
  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = WIDGET_URL;
    s.setAttribute('data-bf-key', WIDGET_KEY);
    document.head.appendChild(s);
  }
  // Identidade verificada: os ids vêm do servidor junto com o userHash que ele
  // assinou — usar outros (ex.: o fallback por e-mail) faria o bFocus recusar.
  const signed = me.bfocus;
  // O papel é uma DECLARAÇÃO sobre quem está falando (vai junto do `product`, que
  // o bFocus exige para resolver o papel): serve para o atendimento e para o
  // agente de IA saberem com quem falam, e ausente quando não reconhecido.
  const role = widgetRole(me.role);
  window.bFocus.init({
    key: WIDGET_KEY,
    user: { externalId: signed?.user_external_id ?? (me.user_id || me.email || 'unknown'), name: me.name || me.email, email: me.email },
    // Nome da CONTA = empresa do cadastro do tenant; fallback p/ o nome do usuário.
    customer: { externalId: signed?.customer_external_id ?? (me.tenant_id || ''), name: me.tenant_name || me.name || me.email },
    userHash: signed?.user_hash,
    product: 'bzapper',
    position: 'bottom-right',
    locale: widgetLocale(me.locale),
    ...(role ? { role } : {}),
    ...(MONITOR_KEY ? {
      monitor: {
        key: MONITOR_KEY,
        release: RELEASE,
        environment: window.location.hostname === PROD_HOSTNAME ? 'production' : 'development',
      },
    } : {}),
  });
}

/** Abre o painel assim que o script estiver pronto. Devolve um cancelador. */
export function bfocusOpenWhenReady(): () => void {
  let tries = 0;
  const id = window.setInterval(() => {
    if (window.bFocus?.open) {
      window.bFocus.open();
      window.clearInterval(id);
    } else if (++tries > 60) {
      window.clearInterval(id);
    }
  }, 150);
  return () => window.clearInterval(id);
}

/** Remove o widget por completo (libera a tela). */
export function bfocusDestroy(): void {
  window.bFocus?.destroy?.();
  document.getElementById(SCRIPT_ID)?.remove();
}
